import { createContext, useState, useEffect, useContext, useLayoutEffect } from 'react';
import 'lodash-es';
import { z } from 'zod';
import { jsx } from 'react/jsx-runtime';

// src/utils/locale.ts
function detectBrowserLocale() {
  try {
    const locale = Intl.DateTimeFormat().resolvedOptions().locale || "en-US";
    return normalizeLocale(locale);
  } catch (error) {
    console.warn("Intl API not available, falling back to navigator.language:", error);
    const browserLang = navigator.language || navigator.userLanguage || "en";
    return normalizeLocale(browserLang);
  }
}
function detectPageLocale() {
  if (typeof document === "undefined") return null;
  try {
    const lang = document.documentElement.lang?.trim();
    if (!lang) return null;
    return normalizeLocale(lang);
  } catch (error) {
    console.warn("document.documentElement.lang not available:", error);
    return null;
  }
}
function resolveAutoDetectedLocale() {
  return detectPageLocale() ?? detectBrowserLocale();
}
function normalizeLocale(locale) {
  const parts = locale.split("-");
  const language = parts[0]?.toLowerCase() || "en";
  const country = parts[1]?.toUpperCase() || language.toUpperCase();
  return `${language}_${country}`;
}
function getBestMatchingLocale(availableLocales, targetLocale) {
  if (availableLocales.includes(targetLocale)) return targetLocale;
  const targetLanguage = targetLocale.split("_")[0];
  const languageMatch = availableLocales.find(
    (locale) => locale.startsWith(targetLanguage + "_")
  );
  if (languageMatch) return languageMatch;
  if (availableLocales.includes("en_US")) return "en_US";
  return availableLocales[0] || null;
}
function findObjectByLocale(objects, targetLocale) {
  if (!objects || objects.length === 0) return null;
  const availableLocales = getAvailableLocales(objects);
  const bestMatch = getBestMatchingLocale(availableLocales, targetLocale);
  if (bestMatch) {
    const matchedObject = objects.find((obj) => obj.locale === bestMatch);
    if (matchedObject) return matchedObject;
  }
  const enUSMatch = objects.find((obj) => obj.locale === "en_US");
  if (enUSMatch) return enUSMatch;
  return objects[0] || null;
}
function getObjectForCurrentLocale(objects, useAutoDetectedLocale = true, configuredLocale = "en_US") {
  const targetLocale = useAutoDetectedLocale ? resolveAutoDetectedLocale() : configuredLocale;
  return findObjectByLocale(objects, targetLocale);
}
function getAvailableLocales(objects) {
  return [...new Set(objects.map((obj) => obj.locale))].sort();
}
function useLocaleDetection(useAutoDetectedLocale = true, configuredLocale = "en_US") {
  const [detectedLocale, setDetectedLocale] = useState(configuredLocale);
  useEffect(() => {
    setDetectedLocale(
      useAutoDetectedLocale ? resolveAutoDetectedLocale() : configuredLocale
    );
  }, [useAutoDetectedLocale, configuredLocale]);
  useEffect(() => {
    if (!useAutoDetectedLocale) return;
    if (typeof document === "undefined") return;
    if (typeof MutationObserver === "undefined") return;
    const observer = new MutationObserver(() => {
      setDetectedLocale(resolveAutoDetectedLocale());
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["lang"]
    });
    return () => observer.disconnect();
  }, [useAutoDetectedLocale]);
  return {
    detectedLocale,
    detectBrowserLocale,
    detectPageLocale,
    getBestMatchingLocale,
    findObjectByLocale,
    getObjectForCurrentLocale,
    getAvailableLocales
  };
}
function useTranslation(store, currentLocale = "en_US") {
  const _t = (key, defaultValue = key) => store?.[key]?.[currentLocale] || store?.[key]?.["en_US"] || defaultValue;
  return { _t };
}

// src/utils/image.ts
function getProxiedImageUrl(imageUrl, width = 64, height = 64) {
  if (!imageUrl) return imageUrl;
  let normalizedUrl = imageUrl;
  if (normalizedUrl.startsWith("//")) {
    normalizedUrl = `https:${normalizedUrl}`;
  } else if (!normalizedUrl.startsWith("http://") && !normalizedUrl.startsWith("https://")) {
    normalizedUrl = `https://${normalizedUrl}`;
  }
  const imageExtensions = /\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)(\?.*)?$/i;
  const hasExtension = imageExtensions.test(normalizedUrl);
  let processedUrl = normalizedUrl;
  if (!hasExtension) {
    const urlParts = normalizedUrl.split("?");
    const baseUrl = urlParts[0];
    const queryString = urlParts[1] ? `?${urlParts[1]}` : "";
    processedUrl = `${baseUrl}-homerunner.jpg${queryString}`;
  }
  return `https://hrimgs.co/w:${width}/h:${height}/rt:fill/plain/${processedUrl}`;
}
var DEFAULT_MARGIN = {
  top: { value: 0, unit: "px" },
  right: { value: 0, unit: "auto" },
  bottom: { value: 0, unit: "px" },
  left: { value: 0, unit: "auto" }
};
var DEFAULT_PADDING = {
  top: { value: 0, unit: "px" },
  right: { value: 0, unit: "px" },
  bottom: { value: 0, unit: "px" },
  left: { value: 0, unit: "px" }
};
var DEFAULT_BASE_SPACING = {
  margin: DEFAULT_MARGIN,
  padding: DEFAULT_PADDING,
  width: { value: 0, unit: "auto" },
  maxWidth: { value: 100, unit: "%" }
};
var marginSideSchema = (unit) => z.object({
  value: z.number().default(0).describe("Length for this side"),
  unit: z.enum(["px", "%", "auto"]).default(unit).describe("Unit for this side; auto centers")
}).default({ value: 0, unit });
var paddingSideSchema = () => z.object({
  value: z.number().default(0).describe("Length for this side"),
  unit: z.enum(["px", "%"]).default("px").describe("Unit for this side")
}).default({ value: 0, unit: "px" });
var marginBoxSchema = (defaults) => z.object({
  top: marginSideSchema(defaults.top.unit),
  right: marginSideSchema(defaults.right.unit),
  bottom: marginSideSchema(defaults.bottom.unit),
  left: marginSideSchema(defaults.left.unit)
}).default(defaults).describe("Outer margin");
var paddingBoxSchema = () => z.object({
  top: paddingSideSchema(),
  right: paddingSideSchema(),
  bottom: paddingSideSchema(),
  left: paddingSideSchema()
}).default(DEFAULT_PADDING).describe("Inner padding");
var widthSchema = (defaults) => z.object({
  value: z.number().default(defaults.value).describe("Width length"),
  unit: z.enum(["px", "%", "auto"]).default(defaults.unit).describe("Width unit; auto leaves width unset")
}).default(defaults).describe("Width of the element");
var maxWidthSchema = (defaults) => z.object({
  value: z.number().default(defaults.value).describe("Maximum width length"),
  unit: z.enum(["px", "%", "none"]).default(defaults.unit).describe("Maximum width unit; none removes the constraint")
}).default(defaults).describe("Maximum width of the element");
z.object({
  margin: marginBoxSchema(DEFAULT_MARGIN),
  padding: paddingBoxSchema(),
  width: widthSchema(DEFAULT_BASE_SPACING.width),
  maxWidth: maxWidthSchema(DEFAULT_BASE_SPACING.maxWidth)
}).default(DEFAULT_BASE_SPACING).describe("Margin, padding, width and maximum width of the widget");

// src/runtime/api.ts
function homerunnerApiBaseUrl() {
  if (typeof window !== "undefined") {
    const fromRuntime = window.HRWidgetRuntime?.apiBaseUrl;
    if (fromRuntime) return fromRuntime;
  }
  const envValue = typeof process !== "undefined" ? process.env?.NEXT_PUBLIC_HOMERUNNER_BASE_URL : void 0;
  if (envValue) return envValue;
  return "https://central.homerunner.io";
}
var WidgetFetchError = class extends Error {
  constructor(status, message, retryAfterMs) {
    super(message);
    this.status = status;
    this.retryAfterMs = retryAfterMs;
    this.name = "WidgetFetchError";
  }
};
function parseRetryAfterMs(headers) {
  const raw = headers?.get?.("Retry-After");
  if (!raw) return void 0;
  const seconds = Number(raw);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1e3);
  const at = Date.parse(raw);
  return Number.isNaN(at) ? void 0 : Math.max(0, at - Date.now());
}
async function fetchFeed(feedId) {
  const res = await fetch(`${homerunnerApiBaseUrl()}/public-api/v1/${feedId}/details`);
  if (!res.ok) {
    throw new WidgetFetchError(
      res.status,
      `Failed to fetch feed (HTTP ${res.status})`,
      parseRetryAfterMs(res.headers)
    );
  }
  const body = await res.json();
  if (body.status_code !== 200) {
    throw new WidgetFetchError(body.status_code, "Failed to fetch feed");
  }
  return body.result;
}
async function fetchWidget(widgetId) {
  const res = await fetch(`${homerunnerApiBaseUrl()}/public-api/v1/widget/${widgetId}`);
  if (!res.ok) {
    throw new WidgetFetchError(
      res.status,
      `Failed to fetch widget (HTTP ${res.status})`,
      parseRetryAfterMs(res.headers)
    );
  }
  const body = await res.json();
  if (!body.success) {
    throw new WidgetFetchError(res.status, "Failed to fetch widget");
  }
  return body.data;
}

// src/utils/query-helpers.ts
var getFeedQuery = (feedId) => ({
  queryKey: ["feed", feedId],
  queryFn: () => fetchFeed(feedId)
});
var getWidgetQuery = (widgetId) => ({
  queryKey: ["widget", widgetId],
  queryFn: () => fetchWidget(widgetId)
});
var useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
var WidgetContext = createContext(void 0);
function setSystemThemeCookie(value) {
  if (typeof document === "undefined") return;
  document.cookie = `hr:widget:system_theme=${value}; path=/; max-age=31536000; SameSite=Lax`;
}
function WidgetProvider({
  children,
  options,
  feedId,
  widgetId,
  widgetType,
  data,
  resolvedTheme
}) {
  const [resolvedThemeState, setResolvedThemeState] = useState(
    resolvedTheme ?? "light"
  );
  useIsomorphicLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      const systemTheme = mediaQuery.matches ? "dark" : "light";
      setSystemThemeCookie(systemTheme);
      if (options.colorScheme === "auto" || options.colorScheme === "global") {
        setResolvedThemeState(systemTheme);
      } else {
        setResolvedThemeState(options.colorScheme);
      }
    };
    mediaQuery.addEventListener("change", handleChange);
    handleChange();
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [options.colorScheme]);
  return /* @__PURE__ */ jsx(
    WidgetContext.Provider,
    {
      value: {
        options,
        feedId,
        widgetId,
        widgetType,
        data,
        resolvedTheme: resolvedThemeState
      },
      children
    }
  );
}
function useWidget() {
  const context = useContext(WidgetContext);
  if (!context) {
    throw new Error("useWidget must be used within a WidgetProvider");
  }
  return context;
}

// src/utils/preload-hints.ts
var DEFAULT_MIN_HEIGHTS = {
  explorer: (options) => {
    const o = options;
    if (o?.search?.heroBar) return "140px";
    if (o?.template === "slider") return "590px";
    return "min(65vh, 640px)";
  },
  "search-bar": "138px",
  booking: "clamp(0px, (100vw - 960px) * 999, 280px)",
  calendar: "570px",
  "multi-calendar": "340px",
  "property-card": "370px",
  "related-properties": "440px",
  gallery: "260px"
};
function preloadMinHeightHint(widgetType, options) {
  const hint = DEFAULT_MIN_HEIGHTS[widgetType];
  if (!hint) return null;
  return typeof hint === "function" ? hint(options) : hint;
}

export { WidgetProvider, detectBrowserLocale, detectPageLocale, findObjectByLocale, getAvailableLocales, getBestMatchingLocale, getFeedQuery, getObjectForCurrentLocale, getProxiedImageUrl, getWidgetQuery, preloadMinHeightHint, useLocaleDetection, useTranslation, useWidget };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map